/**
 * Accès aux données Supabase. Toutes les requêtes passent par la RLS : chaque utilisateur
 * ne lit et n'écrit que ses propres données (ADR 0006).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  clesCartesFilles,
  normaliserCarteMere,
  validerCarteMere,
  type CarteMere,
} from '../cartes/carte-mere';
import { validerTouches } from '../clavier/touches';
import type { EtatFsrs, Note } from '../fsrs/planificateur';
import { validerReglages, REGLAGES_PAR_DEFAUT, type ReglagesPaquet } from '../paquets/reglages';
import {
  carteDepuisLigne,
  carteMereDepuisLigne,
  etatFsrsVersJson,
  ligneDepuisReglages,
  paquetDepuisLigne,
  preferencesDepuisLigne,
  reglagesDepuisLigne,
  revisionDepuisLigne,
  type CarteLue,
  type CarteMereLue,
  type LigneCarteDuPaquet,
  type LigneCarteMere,
  type LignePaquet,
  type LignePreferences,
  type LigneReglages,
  type LigneRevision,
  type Paquet,
  type Preferences,
  type RevisionLue,
} from './conversions';

/** Erreur renvoyée par la base, avec son code PostgreSQL (ex. 40001, 23514, P0002). */
export class ErreurDonnees extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
  ) {
    super(message);
    this.name = 'ErreurDonnees';
  }
}

/** Erreur de validation avant envoi : rien n'a été écrit. */
export class ErreurValidation extends Error {
  constructor(readonly erreurs: string[]) {
    super(erreurs.join(' '));
    this.name = 'ErreurValidation';
  }
}

/** Seau des images des cartes. */
export const SEAU_IMAGES = 'images';

function verifier<T>(reponse: { data: T; error: { message: string; code?: string } | null }): T {
  if (reponse.error !== null) throw new ErreurDonnees(reponse.error.message, reponse.error.code);
  return reponse.data;
}

/**
 * Nombre de lignes demandées par page. L'API renvoie au plus `max_rows` lignes par requête
 * (1000 dans supabase/config.toml, comme sur un projet hébergé) : au-delà, on lit page par
 * page pour ne rien perdre.
 */
export const LIGNES_PAR_PAGE = 1000;

type Reponse = { data: unknown; error: { message: string; code?: string } | null };

/** Toutes les lignes d'une requête triée, lue par pages (`de` et `a` inclus). */
async function toutesLesLignes<T>(page: (de: number, a: number) => PromiseLike<Reponse>): Promise<T[]> {
  const lignes: T[] = [];
  for (let de = 0; ; de += LIGNES_PAR_PAGE) {
    const lot = (verifier(await page(de, de + LIGNES_PAR_PAGE - 1)) ?? []) as T[];
    lignes.push(...lot);
    if (lot.length < LIGNES_PAR_PAGE) return lignes;
  }
}

/** Révision à enregistrer : l'état avant sert au contrôle de concurrence. */
export interface RevisionAEnregistrer {
  carteId: string;
  note: Note;
  reviseLe: Date;
  avant: EtatFsrs;
  apres: EtatFsrs;
  tempsReponseMs: number;
}

export function creerDepot(client: SupabaseClient) {
  return {
    // --- Paquets ---

    async paquets(): Promise<Paquet[]> {
      const lignes = await toutesLesLignes<LignePaquet>((de, a) =>
        client.from('paquets').select('id, parent_id, nom, cree_le').order('nom').order('id').range(de, a),
      );
      return lignes.map(paquetDepuisLigne);
    },

    async creerPaquet(nom: string, parentId: string | null = null): Promise<Paquet> {
      const ligne = verifier(
        await client
          .from('paquets')
          .insert({ nom, parent_id: parentId })
          .select('id, parent_id, nom, cree_le')
          .single(),
      ) as LignePaquet;
      return paquetDepuisLigne(ligne);
    },

    async renommerPaquet(id: string, nom: string): Promise<void> {
      verifier(await client.from('paquets').update({ nom }).eq('id', id));
    },

    /** Range un paquet dans un autre, ou en fait un paquet principal (`null`). */
    async deplacerPaquet(id: string, parentId: string | null): Promise<void> {
      verifier(await client.from('paquets').update({ parent_id: parentId }).eq('id', id));
    },

    /** Supprime le paquet, ses sous-paquets, leurs cartes et leur historique. */
    async supprimerPaquet(id: string): Promise<void> {
      verifier(await client.from('paquets').delete().eq('id', id));
    },

    // --- Réglages d'un paquet principal ---

    async reglages(paquetId: string): Promise<ReglagesPaquet> {
      const ligne = verifier(
        await client.from('reglages_paquets').select('*').eq('paquet_id', paquetId).single(),
      ) as LigneReglages;
      return reglagesDepuisLigne(ligne);
    },

    async modifierReglages(paquetId: string, modifications: Partial<ReglagesPaquet>): Promise<void> {
      const erreurs = validerReglages({ ...REGLAGES_PAR_DEFAUT, ...modifications });
      if (erreurs.length > 0) throw new ErreurValidation(erreurs);
      verifier(
        await client
          .from('reglages_paquets')
          .update(ligneDepuisReglages(modifications))
          .eq('paquet_id', paquetId),
      );
    },

    // --- Cartes ---

    /** Cartes du paquet et de tous ses sous-paquets, suspendues comprises. */
    async cartesDuPaquet(paquetId: string): Promise<CarteLue[]> {
      const lignes = await toutesLesLignes<LigneCarteDuPaquet>((de, a) =>
        client.rpc('cartes_du_paquet', { p_paquet_id: paquetId }).order('id').range(de, a),
      );
      return lignes.map(carteDepuisLigne);
    },

    async carteMere(id: string): Promise<CarteMereLue> {
      const ligne = verifier(
        await client
          .from('cartes_meres')
          .select('id, paquet_id, type, mode, contenu, cree_le, modifie_le')
          .eq('id', id)
          .single(),
      ) as LigneCarteMere;
      return carteMereDepuisLigne(ligne);
    },

    /**
     * Crée (`id` null) ou modifie une carte mère. Les trous sans numéro sont numérotés, les
     * cartes filles synchronisées. Renvoie l'identifiant et le contenu enregistré.
     */
    async enregistrerCarteMere(
      id: string | null,
      paquetId: string,
      saisie: CarteMere,
    ): Promise<{ id: string; carte: CarteMere }> {
      const carte = normaliserCarteMere(saisie);
      const erreurs = validerCarteMere(carte);
      if (erreurs.length > 0) throw new ErreurValidation(erreurs);
      const nouvelId = verifier(
        await client.rpc('enregistrer_carte_mere', {
          p_id: id,
          p_paquet_id: paquetId,
          p_type: carte.type,
          p_mode: carte.mode,
          p_contenu: carte.contenu,
          p_cles: clesCartesFilles(carte),
        }),
      ) as string;
      return { id: nouvelId, carte };
    },

    /** Toutes les cartes mères du compte, dans l'ordre d'ajout (gestion des cartes). */
    async cartesMeres(): Promise<CarteMereLue[]> {
      const lignes = await toutesLesLignes<LigneCarteMere>((de, a) =>
        client
          .from('cartes_meres')
          .select('id, paquet_id, type, mode, contenu, cree_le, modifie_le')
          .order('cree_le')
          .order('id')
          .range(de, a),
      );
      return lignes.map(carteMereDepuisLigne);
    },

    async deplacerCarteMere(id: string, paquetId: string): Promise<void> {
      verifier(await client.from('cartes_meres').update({ paquet_id: paquetId }).eq('id', id));
    },

    /** Supprime la carte mère, ses cartes filles et leur historique. */
    async supprimerCarteMere(id: string): Promise<void> {
      verifier(await client.from('cartes_meres').delete().eq('id', id));
    },

    /** Suspend (sort des séances) ou réactive une carte. */
    async suspendre(carteId: string, suspendue: boolean): Promise<void> {
      verifier(await client.from('cartes').update({ suspendue }).eq('id', carteId));
    },

    // --- Révisions ---

    /** Révisions des cartes du paquet et de ses sous-paquets, depuis une date. */
    async revisionsDuPaquet(paquetId: string, depuis: Date): Promise<RevisionLue[]> {
      const lignes = await toutesLesLignes<LigneRevision>((de, a) =>
        client
          .rpc('revisions_du_paquet', { p_paquet_id: paquetId, p_depuis: depuis.toISOString() })
          .order('id')
          .range(de, a),
      );
      return lignes.map(revisionDepuisLigne);
    },

    /**
     * Enregistre une note (journal + état FSRS de la carte). Refusée avec le code 40001 si
     * la carte a été revue entre-temps. Renvoie l'identifiant de la révision.
     */
    async enregistrerRevision(revision: RevisionAEnregistrer): Promise<number> {
      return verifier(
        await client.rpc('enregistrer_revision', {
          p_carte_id: revision.carteId,
          p_reps_avant: revision.avant.reps,
          p_note: revision.note,
          p_revise_le: revision.reviseLe.toISOString(),
          p_temps_reponse_ms: Math.max(0, Math.round(revision.tempsReponseMs)),
          p_carte_apres: etatFsrsVersJson(revision.apres),
        }),
      ) as number;
    },

    /** Annule la dernière révision d'une carte et restaure son état FSRS d'avant. */
    async annulerRevision(revisionId: number): Promise<void> {
      verifier(await client.rpc('annuler_revision', { p_revision_id: revisionId }));
    },

    // --- Préférences du compte ---

    async preferences(): Promise<Preferences> {
      const ligne = verifier(
        await client
          .from('preferences_compte')
          .select('touches, derniers_modes, couleurs_surlignage')
          .single(),
      ) as LignePreferences;
      return preferencesDepuisLigne(ligne);
    },

    async modifierPreferences(
      modifications: Partial<Pick<Preferences, 'touches' | 'couleursSurlignage'>>,
    ): Promise<void> {
      if (modifications.touches !== undefined) {
        const erreurs = validerTouches(modifications.touches);
        if (erreurs.length > 0) throw new ErreurValidation(erreurs);
      }
      const ligne: Partial<LignePreferences> = {};
      if (modifications.touches !== undefined) ligne.touches = modifications.touches;
      if (modifications.couleursSurlignage !== undefined) {
        ligne.couleurs_surlignage = modifications.couleursSurlignage;
      }
      const { data } = await client.auth.getUser();
      if (data.user === null) throw new ErreurDonnees('Connexion requise.', '42501');
      verifier(await client.from('preferences_compte').update(ligne).eq('user_id', data.user.id));
    },

    // --- Images ---

    /**
     * Envoie une image (déjà compressée) dans le dossier de l'utilisateur. Renvoie son
     * chemin, à enregistrer dans le contenu de la carte.
     */
    async envoyerImage(image: Blob, extension: string): Promise<string> {
      const { data } = await client.auth.getUser();
      if (data.user === null) throw new ErreurDonnees('Connexion requise.', '42501');
      const chemin = `${data.user.id}/${crypto.randomUUID()}.${extension.replace(/^\./, '')}`;
      verifier(
        await client.storage
          .from(SEAU_IMAGES)
          .upload(chemin, image, { contentType: image.type, upsert: false }),
      );
      return chemin;
    },

    /** Adresse temporaire d'une image (seau privé). */
    async adresseImage(chemin: string, dureeSecondes = 3600): Promise<string> {
      const resultat = verifier(
        await client.storage.from(SEAU_IMAGES).createSignedUrl(chemin, dureeSecondes),
      );
      if (resultat === null) throw new ErreurDonnees('Image introuvable.', undefined);
      return resultat.signedUrl;
    },
  };
}

export type Depot = ReturnType<typeof creerDepot>;
